import { Controller } from "@hotwired/stimulus"

// Passkey (WebAuthn) registration and login.
//
// Usage (registration, signed-in user):
//   <div data-controller="webauthn"
//        data-webauthn-options-url-value="/users/passkeys/options"
//        data-webauthn-create-url-value="/users/passkeys">
//     <input data-webauthn-target="nickname">
//     <button data-action="webauthn#register">Passkey anlegen</button>
//     <div data-webauthn-target="error" class="d-none"></div>
//     <div data-webauthn-target="recoverySection" class="d-none">
//       <ol data-webauthn-target="recoveryCodes"></ol>
//     </div>
//   </div>
//
// Usage (login):
//   <div data-controller="webauthn"
//        data-webauthn-options-url-value="/users/passkey_sessions/options"
//        data-webauthn-create-url-value="/users/passkey_sessions">
//     <button data-action="webauthn#authenticate">Mit Passkey anmelden</button>
//     <div data-webauthn-target="error" class="d-none"></div>
//   </div>
export default class extends Controller {
  static targets = ["error", "nickname", "recoverySection", "recoveryCodes", "form", "continueLink"]
  static values = {
    optionsUrl: String,
    createUrl: String,
    continueUrl: String,
    conditional: Boolean
  }

  // With conditional: true the browser offers matching passkeys directly in
  // the autofill dropdown of fields marked autocomplete="username webauthn" —
  // login works even in browsers we haven't flagged with the kk_passkey cookie.
  connect() {
    if (this.conditionalValue) this.conditionalAuthenticate()
  }

  disconnect() {
    this.abortController?.abort()
  }

  async conditionalAuthenticate() {
    try {
      if (!window.PublicKeyCredential?.isConditionalMediationAvailable) return
      if (!(await PublicKeyCredential.isConditionalMediationAvailable())) return

      this.abortController = new AbortController()
      await this.performAuthentication({ mediation: "conditional", signal: this.abortController.signal })
    } catch (error) {
      // aborted by a modal login or page leave, or the user dismissed the
      // autofill suggestion — never surface errors from the passive flow
      console.debug("Conditional WebAuthn not completed:", error)
    }
  }

  async register(event) {
    event.preventDefault()
    if (!this.supported()) return

    try {
      const options = await this.fetchJSON(this.optionsUrlValue, {})

      const publicKey = {
        ...options,
        challenge: base64urlToBuffer(options.challenge),
        user: { ...options.user, id: base64urlToBuffer(options.user.id) },
        excludeCredentials: (options.excludeCredentials || []).map((c) => ({
          ...c,
          id: base64urlToBuffer(c.id)
        }))
      }

      const credential = await navigator.credentials.create({ publicKey })
      const response = credential.response

      const payload = {
        nickname: this.hasNicknameTarget ? this.nicknameTarget.value : null,
        continue_to: this.continueUrlValue || null,
        credential: {
          type: credential.type,
          id: credential.id,
          rawId: bufferToBase64url(credential.rawId),
          authenticatorAttachment: credential.authenticatorAttachment,
          response: {
            attestationObject: bufferToBase64url(response.attestationObject),
            clientDataJSON: bufferToBase64url(response.clientDataJSON),
            transports: response.getTransports ? response.getTransports() : []
          }
        }
      }

      const result = await this.fetchJSON(this.createUrlValue, payload)

      if (result.recovery_codes && result.recovery_codes.length > 0) {
        this.showRecoveryCodes(result.recovery_codes, result.redirect_url)
      } else {
        window.location.assign(result.redirect_url || window.location.href)
      }
    } catch (error) {
      this.showError(error)
    }
  }

  async authenticate(event) {
    event.preventDefault()
    if (!this.supported()) return

    // a pending conditional (autofill) request must be cancelled first —
    // browsers allow only one WebAuthn request at a time
    this.abortController?.abort()

    try {
      await this.performAuthentication({})
    } catch (error) {
      this.showError(error)
    }
  }

  async performAuthentication({ mediation, signal }) {
    const options = await this.fetchJSON(this.optionsUrlValue, {})

    const publicKey = {
      ...options,
      challenge: base64urlToBuffer(options.challenge),
      allowCredentials: (options.allowCredentials || []).map((c) => ({
        ...c,
        id: base64urlToBuffer(c.id)
      }))
    }

    const credential = await navigator.credentials.get({ publicKey, mediation, signal })
    const response = credential.response

    const payload = {
      credential: {
        type: credential.type,
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        response: {
          clientDataJSON: bufferToBase64url(response.clientDataJSON),
          authenticatorData: bufferToBase64url(response.authenticatorData),
          signature: bufferToBase64url(response.signature),
          userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null
        }
      }
    }

    const result = await this.fetchJSON(this.createUrlValue, payload)
    window.location.assign(result.redirect_url || "/")
  }

  // --- helpers ---------------------------------------------------------------

  supported() {
    if (window.PublicKeyCredential) return true
    this.showError(new Error("Dein Browser unterstützt leider keine Passkeys."))
    return false
  }

  async fetchJSON(url, body) {
    const response = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
      },
      body: JSON.stringify(body)
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(data.error || "Es ist ein Fehler aufgetreten. Bitte versuche es erneut.")
    }
    return data
  }

  // The codes are shown exactly once — the targets are required markup in
  // _add_passkey.html.erb; Stimulus raises loudly if they are missing.
  showRecoveryCodes(codes, redirectUrl) {
    this.formTarget.classList.add("d-none")
    this.recoveryCodesTarget.innerHTML = ""
    codes.forEach((code) => {
      const item = document.createElement("li")
      item.textContent = code
      this.recoveryCodesTarget.appendChild(item)
    })
    if (redirectUrl) this.continueLinkTarget.href = redirectUrl
    this.recoverySectionTarget.classList.remove("d-none")
  }

  showError(error) {
    // The user cancelling the browser dialog is not an error worth showing
    if (error.name === "NotAllowedError" || error.name === "AbortError") return

    if (this.hasErrorTarget) {
      this.errorTarget.textContent = error.message
      this.errorTarget.classList.remove("d-none")
    } else {
      alert(error.message)
    }
  }
}

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/")
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(padded)
  const buffer = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) buffer[i] = binary.charCodeAt(i)
  return buffer.buffer
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  bytes.forEach((b) => { binary += String.fromCharCode(b) })
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}
